import React, { useState, useContext, useEffect, useMemo } from "react";
import { SubjectDataContext } from "./SubjectRoot";
import PerformanceScoreGraph from "../components/Graphs/PerformanceScoreGraph";
import LineGraph from "../components/Graphs/LineGraph";
import NormsBarGraph from "../components/Graphs/Norms/NormsBarGraph";
import NormTable from "../components/Graphs/Norms/NormTable";
import Global from "../utils/Global";
import Trial from "../utils/Trial";
import PerformanceScores from "../utils/PerformanceScores";
import { FiAlertTriangle } from "react-icons/fi";
import patient_account_types from "../config/patient_account_types";
import account_status from "../config/account_status";

export default function ManageSubject() {
  // Get common data from context
  const { subject, norm, refreshData, setIsLoading } = useContext(SubjectDataContext);

  // Local state for this component
  const [isTabLoading, setIsTabLoading] = useState(true);
  const [performanceScores, setPerformanceScores] = useState(null);
  const [startMetrics, setStartMetrics] = useState(null);
  const [currentMetrics, setCurrentMetrics] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [hoverPhantom, setHoverPhantom] = useState(false);
  const [persistentPhantom, setPersistentPhantom] = useState(false);
  const [sentConfirmationCode, setSentConfirmationCode] = useState(false);
  const [successfullySentConfirmationCode, setSuccessfullySentConfirmationCode] = useState(null);
  const showPhantom = hoverPhantom || persistentPhantom;

  // Fetch tab-specific data
  useEffect(() => {
    const fetchTabData = async () => {
      try {
        setIsTabLoading(true);
        
        if (norm?.all_valid) {
          await fetchPerformanceScoresData();
        } else {
          await fetchMetricsData();
        }
      } catch (error) {
        console.error("Error fetching tab-specific data:", error);
      } finally {
        setIsTabLoading(false);
      }
    };
    
    // Separate function to handle performance scores fetching
    const fetchPerformanceScoresData = async () => {
      const performanceScores = await Global.localAppHandler.app_retrieve_performance_scores();
      const extractedScores = PerformanceScores.extractScoresFromList(performanceScores.performance_scores);
      setPerformanceScores(extractedScores);
      
      // Only fetch start/end metrics if we have dates
      if (extractedScores.dates.length > 0) {
        await fetchStartEndMetrics(extractedScores.dates[0], extractedScores.dates[extractedScores.dates.length - 1]);
      }
    };
    
    // Separate function to handle metrics fetching
    const fetchMetricsData = async () => {
      const trialList = await Global.localAppHandler.app_get_trial_list({ include_metrics: true });
      const extractedMetrics = Trial.extractMetricsFromList(trialList);
      setMetrics(extractedMetrics);
      
      // Only fetch start/end metrics if we have dates
      if (extractedMetrics.dates.length > 0) {
        await fetchStartEndMetrics(extractedMetrics.dates[0], extractedMetrics.dates[extractedMetrics.dates.length - 1]);
      }
    };
    
    // Common function for fetching start/end metrics
    const fetchStartEndMetrics = async (startDate, endDate) => {
      const startEndTrialList = await Global.localAppHandler.app_get_start_end_trial_list({
        include_metrics: true,
        start_date: startDate,
        end_date: endDate,
      });
      
      const startMetrics = Trial.extractAveragedMetricsFromList(startEndTrialList.start_trials);
      const currentMetrics = Trial.extractAveragedMetricsFromList(startEndTrialList.current_trials);
      
      setStartMetrics(startMetrics);
      setCurrentMetrics(currentMetrics);
    };

    // Only fetch if we have the necessary prerequisites
    if (norm !== undefined) {
      fetchTabData();
    }
  }, [norm, refreshData]);

  // Calculate date ranges for graphs
  const [minDate, maxDate, paddedMinDateStr, paddedMaxDateStr, trialNames] = useMemo(() => {
    let dates;
    let trialNames;
    if (!performanceScores && !metrics) {
      dates = [];
    } else {
      if (performanceScores) {
        dates = performanceScores.dates.map((label) => new Date(`${label}T00:00:00`)).filter((date) => !isNaN(date.getTime()));
        trialNames = performanceScores.scores.map((score) => PerformanceScores.getClassificationFromScore(score).label);
      } else {
        dates = metrics?.dates.map((label) => new Date(`${label}T00:00:00`)).filter((date) => !isNaN(date.getTime())) || [];
        trialNames = metrics?.trial_names;
      }
    }
    
    let min, max;
    if (dates.length > 0) {
      min = new Date(Math.min(...dates.map((date) => date.getTime())));
      max = new Date(Math.max(...dates.map((date) => date.getTime())));
    } else {
      min = new Date();
      max = new Date();
    }
    
    const range = max.getTime() - min.getTime();
    let padding = (0.2 * range) / (1000 * 60 * 60 * 24);

    if (padding < 2) {
      padding = 2;
    }
    
    let minDatePadded = new Date(min);
    minDatePadded.setDate(minDatePadded.getDate() - padding);

    let maxDatePadded = new Date(max);
    maxDatePadded.setDate(maxDatePadded.getDate() + padding);

    // const trialNames = dates.map((date) => 
    //   date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    // );

    return [min, max, minDatePadded.toISOString(), maxDatePadded.toISOString(), trialNames];
  }, [performanceScores, metrics]);

  // Show loading state while fetching tab-specific data
  if (isTabLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px", marginTop: "100px" }}>
        <div className="text-center">
          <div className="spinner-border mb-3" role="status" style={{ width: "3rem", height: "3rem" }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading patient data...</p>
        </div>
      </div>
    );
  }

  const handleResendConfirmationCode = () => {
    if (sentConfirmationCode) {
      return;
    }
    setSentConfirmationCode(true);
    Global.localAppHandler
      .app_resend_confirmation_code()
      .then((returnStatus) => {
        setSuccessfullySentConfirmationCode(true);
      })
      .catch((err) => {
        setSuccessfullySentConfirmationCode(false);
        console.log(err);
      });
  };

  return (
    <div className="px-4">
      {subject?.account_type === patient_account_types.remote && subject?.account_status === account_status.pending ? (
        <div className="alert alert-primary mt-4">
          {sentConfirmationCode ? (
            successfullySentConfirmationCode === null ? (
              "Sending..."
            ) : successfullySentConfirmationCode ? (
              "Confirmation code resent!"
            ) : (
              "Error sending confirmation code. Refresh page and try again."
            )
          ) : (
            <>
              <FiAlertTriangle className="mb-1" style={{ marginRight: "10px" }} />
              {"Patient has not yet redeemed account. Pending for " + subject?.days_pending + " days. Make sure contact phone is correct."}
              <span
                className="link-primary"
                onClick={handleResendConfirmationCode}
                style={{ textDecoration: "underline", marginLeft: "5px", cursor: "pointer" }}
              >
                Resend Confirmation Code
              </span>
            </>
          )}
        </div>
      ) : (
        <></>
      )}
      <div className="row">
        <div className="col-md-8 mx-auto">
          {norm === undefined ? (
            <></>
          ) : (
            <>
              {norm.all_valid ? (
                <>
                  <PerformanceScoreGraph
                    labels={performanceScores ? performanceScores.dates : []}
                    data={performanceScores ? performanceScores.scores : []}
                    trialNames={trialNames ? trialNames : []}
                    lastUpdatedTime={""}
                    timeAxis={"day"}
                    minDate={minDate}
                    maxDate={maxDate}
                    paddedMinDateStr={paddedMinDateStr}
                    paddedMaxDateStr={paddedMaxDateStr}
                  />
                </>
              ) : (
                <>
                  <LineGraph
                    labels={metrics ? metrics.dates : []}
                    data={metrics ? metrics.vas_scores : []}
                    trialNames={metrics ? metrics.trial_names : []}
                    lastUpdatedTime={""}
                    timeAxis={"day"}
                    minDate={minDate}
                    maxDate={maxDate}
                    paddedMinDateStr={paddedMinDateStr}
                    paddedMaxDateStr={paddedMaxDateStr}
                    title={"VAS Pain Scores"}
                    description={"For every walking data collection, pain on a scale of 0 to 10 was reported."}
                  />
                </>
              )}
            </>
          )}
        </div>
        <div className="col-md-4 mx-auto">
          {norm === undefined ? (
            <></>
          ) : (
            <>
              {norm.all_valid ? (
                <>
                  <div className="card mb-4">
                    <div className="d-flex card-header align-items-center justify-content-between">
                      <div className="d-flex flex-column">
                        <span>Current Averages</span>
                        <span className="text-muted small">Based on Recent Collections</span>
                      </div>
                      <div
                        className="btn btn-primary"
                        onMouseEnter={() => setHoverPhantom(true)}
                        onMouseLeave={() => setHoverPhantom(false)}
                        onClick={() => setPersistentPhantom((prev) => !prev)}
                      >
                        {persistentPhantom ? "Hide Starting Avg" : "Show Starting Avg"}
                      </div>
                    </div>
                    <div className="card-body">
                      <NormsBarGraph
                        label="Stride Time"
                        min={norm?.stride_time.range.extreme_low}
                        max={norm?.stride_time.range.extreme_high}
                        normalMin={norm?.stride_time.range.low}
                        normalMax={norm?.stride_time.range.high}
                        currentValue={currentMetrics?.stride_time}
                        phantomValue={startMetrics?.stride_time}
                        showPhantom={showPhantom}
                        unit="ms"
                      />
                      <NormsBarGraph
                        label="Stance Time"
                        min={norm?.stance_time.range.extreme_low}
                        max={norm?.stance_time.range.extreme_high}
                        normalMin={norm?.stance_time.range.low}
                        normalMax={norm?.stance_time.range.high}
                        currentValue={currentMetrics?.stance_time}
                        phantomValue={startMetrics?.stance_time}
                        showPhantom={showPhantom}
                        unit="ms"
                      />
                      <NormsBarGraph
                        label="Stance ASI"
                        min={0}
                        max={10}
                        normalMin={norm?.stance_asi.range.low}
                        normalMax={norm?.stance_asi.range.high}
                        currentValue={currentMetrics?.stance_asi}
                        phantomValue={startMetrics?.stance_asi}
                        showPhantom={showPhantom}
                        unit="%"
                      />
                      <NormsBarGraph
                        label="Swing ASI"
                        min={0}
                        max={10}
                        normalMin={norm?.swing_asi.range.low}
                        normalMax={norm?.swing_asi.range.high}
                        currentValue={currentMetrics?.swing_asi}
                        phantomValue={startMetrics?.swing_asi}
                        showPhantom={showPhantom}
                        unit="%"
                      />
                      <NormsBarGraph
                        label="VAS Pain"
                        min={0}
                        max={10}
                        normalMin={0}
                        normalMax={0}
                        currentValue={currentMetrics?.vas_score}
                        phantomValue={startMetrics?.vas_score}
                        showPhantom={showPhantom}
                        unit=""
                      />
                      <NormsLegend />
                    </div>
                    <div className="card-footer" />
                  </div>
                </>
              ) : (
                <>
                  <div className="card mb-4">
                    <div className="d-flex card-header align-items-center justify-content-between">
                      <span>Current Averages</span>
                      <span className="text-muted small">Based on Recent Collections</span>
                      </div>
                    <div className="card-body">
                      <NormTable label={"Stride Time"} currentValue={currentMetrics?.stride_time} startValue={startMetrics?.stride_time} unit={"ms"} />
                      <NormTable label={"Stance Time"} currentValue={currentMetrics?.stance_time} startValue={startMetrics?.stance_time} unit={"ms"} />
                      <NormTable label={"Stance ASI"} currentValue={currentMetrics?.stance_asi} startValue={startMetrics?.stance_asi} unit={"%"} />
                      <NormTable label={"Swing ASI"} currentValue={currentMetrics?.swing_asi} startValue={startMetrics?.swing_asi} unit={"%"} />
                      <NormTable label={"VAS Pain"} currentValue={currentMetrics?.vas_score} startValue={startMetrics?.vas_score} />
                    </div>
                    <div className="card-footer" />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
      {norm !== undefined && !norm.all_valid ? (
        <div className="row">
          <div className="col-md-12">
            <LineGraph
              labels={metrics ? metrics.dates : []}
              data={metrics ? metrics.stride_times : []}
              trialNames={metrics ? metrics.trial_names : []}
              lastUpdatedTime={""}
              timeAxis={"day"}
              minDate={minDate}
              maxDate={maxDate}
              paddedMinDateStr={paddedMinDateStr}
              paddedMaxDateStr={paddedMaxDateStr}
              norm={norm?.stride_time}
              title={"Stride Times"}
              description={
                "Stride time is taken for a full gait cycle – or foot contact of a limb to the following foot contact of the same limb. Stride time typically decreases with an increase in walking speed."
              }
              unit={" ms"}
            />
          </div>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
}

const NormsLegend = () => {
  return (
    <div className="d-flex justify-content-center pt-3">
      <div className="d-flex align-items-center me-4">
        <div
          style={{
            width: "7px",
            height: "16px",
            backgroundImage: "repeating-linear-gradient(10deg, #000, #000 2px, transparent 2px, transparent 4px)",
            borderRadius: "3px",
            border: "1px solid #000",
            marginRight: "6px",
          }}
        ></div>
        <span style={{ fontSize: "0.85rem" }}>Starting Average</span>
      </div>
      <div className="d-flex align-items-center">
        <div
          style={{
            width: "7px",
            height: "16px",
            backgroundColor: "#000",
            borderRadius: "3px",
            marginRight: "6px",
          }}
        ></div>
        <span style={{ fontSize: "0.85rem" }}>Current Average</span>
      </div>
    </div>
  );
};
